import { useEffect, useRef, useState } from "react";
import { ResetFiltersButton } from "@/components/common/ResetFiltersButton";
import { useSearchParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUser } from "@/context/UserContext";
import { api } from "@/lib/api";
import { OverviewPanel } from "@/components/command/OverviewPanel";
import { TaskBoard } from "@/components/command/tasks/TaskBoard";
import { TasksPanel } from "@/components/command/tasks/TasksPanel";
import { LaunchCountdown } from "@/components/launch/LaunchCountdown";
import { PrioritiesPanel } from "@/components/command/PrioritiesPanel";
import { BlockersPanel } from "@/components/command/BlockersPanel";
import { DecisionsPanel } from "@/components/command/DecisionsPanel";
import { UpdatesPanel } from "@/components/command/UpdatesPanel";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/bc84386f-1b7b-4921-a324-fcf4509e7717/images/c366a2ca1683727aea5ed18b73a64cac823894d30eb2c4999db6832652709437.jpeg";
const MASCOT = "/commando-mascot-v2.webp";

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "tasks", label: "Tasks" },
  { value: "priorities", label: "Priorities" },
  { value: "blockers", label: "Blockers" },
  { value: "decisions", label: "Decisions" },
  { value: "updates", label: "Updates" },
];

// Legacy tabs re-homed to the Office & Team section (/office). Old links redirect.
const MOVED_TABS = { notebook: "notebook", office: "schedule", "office-schedule": "schedule", weekly: "weekly-review", "weekly-review": "weekly-review" };

function ExpeditionHero({ name }) {
  return (
    <motion.section
      data-testid="command-hero"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="relative overflow-hidden rounded-3xl border border-[#E4E9F3] shadow-[0_10px_40px_rgba(11,30,91,0.18)]"
    >
      <img
        src={HERO_BG}
        alt="The Edjuverse — worlds of learning"
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1E5B] via-[#0B1E5B]/80 to-[#0B1E5B]/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1E5B]/60 via-transparent to-transparent" />
      <img
        src={MASCOT}
        alt="Edju Cat in commando-style War Room uniform"
        className="pointer-events-none absolute bottom-0 right-2 z-10 h-[82%] object-contain object-bottom drop-shadow-[0_10px_26px_rgba(0,0,0,0.4)] sm:right-6 sm:h-[92%] lg:right-12"
      />

      <div className="relative z-20 flex min-h-[240px] flex-col justify-center gap-3 p-6 sm:min-h-[290px] sm:p-9 lg:max-w-[60%]">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#67E8F9]/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#67E8F9] ring-1 ring-[#67E8F9]/30">
          <Compass size={13} /> War Room
        </span>
        <h1 className="font-heading text-3xl font-bold leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
          Edju Cat War Room
        </h1>
        <p className="max-w-lg text-sm leading-relaxed text-[#D6DEF6] sm:text-base">
          Building the next learning adventure. Steer priorities, blockers and decisions
          across the Edjuverse — all in one command deck, {name}.
        </p>
      </div>
    </motion.section>
  );
}

export default function CommandCentre() {
  const { currentUser } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const filter = searchParams.get("filter");
  const taskId = searchParams.get("task");
  const tview = searchParams.get("tview");
  const who = searchParams.get("who");
  const validTab = rawTab && TABS.some((t) => t.value === rawTab) ? rawTab : null;
  const [tab, setTab] = useState(validTab || "overview");
  const [openCount, setOpenCount] = useState(null);
  const tabsRef = useRef(null);
  const scrollToTabs = () => setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);

  const clearTaskParam = () => { const p = new URLSearchParams(searchParams); p.delete("task"); setSearchParams(p, { replace: true }); };
  const clearParams = (keys) => { const p = new URLSearchParams(searchParams); keys.forEach((k) => p.delete(k)); setSearchParams(p); };

  // One-way sync: URL tab -> local state. Intentionally NOT depending on `tab`,
  // otherwise internal setTab calls would be forced back to the URL value.
  useEffect(() => {
    if (validTab && validTab !== tab) setTab(validTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validTab]);

  const changeTab = (v) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    next.delete("filter");
    next.delete("task");
    next.delete("tview");
    next.delete("who");
    next.delete("blocker");
    setSearchParams(next);
  };

  const openOverdue = (founder) => {
    setTab("tasks");
    const next = new URLSearchParams(searchParams);
    next.set("tab", "tasks");
    next.set("tview", "overdue");
    if (founder) next.set("who", founder); else next.delete("who");
    next.delete("filter");
    next.delete("task");
    setSearchParams(next);
    scrollToTabs();
  };

  useEffect(() => {
    api.tasksSummary().then((s) => setOpenCount(s.open_total)).catch(() => {});
  }, [tab]);

  // Redirect legacy Command Centre deep links for re-homed features to Office & Team.
  if (rawTab && MOVED_TABS[rawTab]) {
    const p = new URLSearchParams(searchParams);
    p.set("tab", MOVED_TABS[rawTab]);
    return <Navigate to={`/office?${p.toString()}`} replace />;
  }

  return (
    <div className="space-y-6">
      <ExpeditionHero name={currentUser.name} />

      <LaunchCountdown variant="command" />

      <TaskBoard onViewAll={() => { changeTab("tasks"); scrollToTabs(); }} onViewOverdue={openOverdue} />

      <div ref={tabsRef} className="scroll-mt-4" /><ResetFiltersButton /><Tabs value={tab} onValueChange={changeTab} className="w-full pt-2">
        <TabsList
          data-testid="command-tabs"
          className="mb-6 flex h-auto flex-wrap justify-start gap-1.5 rounded-full border border-[#E4E9F3] bg-white p-1.5 shadow-sm"
        >
          {TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              data-testid={`tab-${t.value}`}
              className="rounded-full px-4 py-1.5 text-sm font-semibold text-[#5A6B8C] transition-colors data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=active]:[background-color:var(--accent-primary)]"
            >
              {t.label}
              {t.value === "tasks" && openCount > 0 && (
                <span data-testid="tasks-tab-count" className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[#06B6D4] px-1.5 text-[10px] font-bold leading-4 text-white group-data-[state=active]:bg-white/25">{openCount}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview"><OverviewPanel refreshKey={tab} /></TabsContent>
        <TabsContent value="tasks"><TasksPanel openTaskId={taskId} onTaskConsumed={clearTaskParam} initialView={tview} initialAssignee={who} onNavConsumed={() => clearParams(["tview", "who"])} /></TabsContent>
        <TabsContent value="priorities"><PrioritiesPanel filter={filter} /></TabsContent>
        <TabsContent value="blockers"><BlockersPanel filter={filter} /></TabsContent>
        <TabsContent value="decisions"><DecisionsPanel filter={filter} /></TabsContent>
        <TabsContent value="updates"><UpdatesPanel filter={filter} /></TabsContent>
      </Tabs>
    </div>
  );
}
